import { DesignData } from '../types';

import isFlatDesign from './isFlatDesign';

// Multipage products come in two structural shapes:
// - "spread books" (e.g. photobooks, saddle-stitched books): single-page
//   cover(s) plus 2-page inside spreads.
// - "flat books" (e.g. brochures, calendars): every spread is a single page.
// A design is flat when none of its spreads holds more than one page.
//
// For flat books page_count is exactly the number of pages, so it is derived
// straight from the structure. For spread books there is NO reliable
// structure -> page_count formula: the gap between physical pages and page_count
// is a per-product constant (photobooks drop 3 pages, saddle-stitched books drop
// 4), and two products with an identical spread layout can still disagree — an
// odd-page saddle-stitched book (one cover + N 2-page spreads) looks exactly
// like a photobook yet counts differently. So page_count is trusted from the
// payload on load and only *adjusted* here by the number of pages a mutation
// adds or removes (pageCountDelta), never recomputed from the layout.
function updateFlatDesignPages(design: DesignData) {
  let page_nr = 1;

  design.spreads.forEach((spread) => {
    spread.name = `${page_nr}`;
    // A flat book is homogeneous: every spread is a single, interchangeable
    // page, so each one is a valid template and freely addable/removable.
    // Derive the flags from that structure (as updateSpreadBookDesignPages does
    // from page position) rather than trusting the payload — flat designs arrive
    // without these flags, which is the bug this path fixes.
    spread.isTemplate = true;
    spread.canAddAfter = true;
    spread.canRemove = true;

    spread.pages.forEach((page) => {
      page.spread = spread.name;
      page.page_nr = page_nr;
      page_nr += 1;
    });
  });

  design.page_count = page_nr - 1;
}

function updateSpreadBookDesignPages(design: DesignData, pageCountDelta: number) {
  let page_nr = 1;

  for (let i = 0; i < design.spreads.length; i += 1) {
    const spread = design.spreads[i];
    if (page_nr === 1) {
      spread.name = 'Cover';
    } else {
      const spread_nr = Math.floor(page_nr / 2);
      spread.name = `Inside ${spread_nr}`;
      spread.canRemove = false;
      spread.canAddAfter = false;

      if (page_nr === 2 || page_nr === 3) {
        spread.canAddAfter = true;
      } else if (page_nr === 4 || page_nr === 5) {
        spread.isTemplate = true;
        spread.canAddAfter = true;
        spread.canRemove = true;
      } else if (page_nr < design.spreads.length * 2 - 2) {
        spread.canAddAfter = true;
        spread.canRemove = true;
      }
    }

    spread.pages.forEach((page) => {
      page.spread = spread.name;
      page.page_nr = page_nr;
      page_nr += 1;
    });
  }

  // Adjust the trusted page_count by the pages this mutation changed rather than
  // recomputing it from the layout — see the note above for why the layout alone
  // is not enough. A delta of 0 (e.g. reordering) leaves it untouched.
  design.page_count = (design.page_count ?? 0) + pageCountDelta;
}

// pageCountDelta is the signed number of pages the current mutation added
// (positive) or removed (negative); pass 0 (the default) for operations that
// reshuffle spreads without changing the total, such as reordering.
function updateDesignPagesUtil(design: DesignData, pageCountDelta = 0) {
  if (isFlatDesign(design)) {
    updateFlatDesignPages(design);
  } else {
    updateSpreadBookDesignPages(design, pageCountDelta);
  }
}

export default updateDesignPagesUtil;
